import { utcToZonedTime, format } from "date-fns-tz";
import i18next from "i18next";
import { isValidIBAN } from "ibantools";
import { de, fr, enGB, it } from "date-fns/locale";
import { IBAN_TYPE, PAYMENT_INFO_TYPE, WORKFLOWUSER_ROLE } from "./InvoiceEnums";
import type { IQRExtractInvoiceData } from "./InvoiceTypes";
import { formatCurrency } from "src/utils/Currency";

/**
 * Formats a given date string into a human-readable date format.
 *
 * @param {string} dateString - The date string to be formatted, expected in the format "YYYY-MM-DD".
 * @param {boolean} [short=false] - If true, formats the date in a short format "d.M.yy" ("18.6.23").
 * @param {boolean} [displayTime=false] - If true, formats the date with a timestamp "18. Juni 2023 - 12:34"
 *
 * @returns {string} - The formatted date string.
 *
 * Examples:
 *
 * formatDate("2023-06-18", true)
 * // Returns: "18.6.23" (short format)
 *
 * formatDate("2023-06-18")
 * // Returns: "18. Juni 2023" (long format, assuming 'i18next.language' is set to "de")
 */
export const formatDate = (dateString: string, short = false, displayTime = false) => {
    const date = new Date(dateString);

    if (short) {
        return format(date, "d.M.yy");
    }

    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric"
    };

    const formattedDate = date.toLocaleDateString(i18next.language, options);
    const [, monthName, year] = formattedDate.split(" ");
    const dayNumber = date.getDate();

    if (displayTime) {
        const time = format(date, "HH.mm");
        return `${dayNumber}. ${monthName} ${year} - ${time}`;
    }

    return `${dayNumber}. ${monthName} ${year}`;
};

export const formatDateForImage = (date: Date) => {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS");
};

export const formatDateForElement = (date: Date) => {
    return format(date, "yyyy-MM-dd");
};

export const formatDateHistoryEntry = (dateString: string, adjustTimezoneUtcToZH: boolean) => {
    const dateFormat = "d.M.yy HH:mm";

    if (adjustTimezoneUtcToZH) {
        const timeZone = "Europe/Zurich";
        const zonedDate = utcToZonedTime(dateString + "Z", timeZone);
        return format(zonedDate, dateFormat, { timeZone });
    } else {
        return format(new Date(dateString), dateFormat);
    }
};

export const getTimeStampForHistoryEntry = (dateString: string, adjustTimezoneUtcToZH: boolean) => {
    if (adjustTimezoneUtcToZH) {
        const timeZone = "Europe/Zurich";
        const zonedDate = utcToZonedTime(dateString + "Z", timeZone);
        return zonedDate;
    } else {
        return new Date(dateString);
    }
};

// As described in 'Schweizer Implementation Guidelines QR-Rechnung'
// Anhang B: Prüfzifferberechnung nach Modulo 10 rekursiv
// https://www.six-group.com/dam/download/banking-services/standardization/qr-bill/ig-qr-bill-v2.2-de.pdf

export const PRUEFZIFFER_MATRIX: number[][] = [
    [0, 9, 4, 6, 8, 2, 7, 1, 3, 5],
    [9, 4, 6, 8, 2, 7, 1, 3, 5, 0],
    [4, 6, 8, 2, 7, 1, 3, 5, 0, 9],
    [6, 8, 2, 7, 1, 3, 5, 0, 9, 4],
    [8, 2, 7, 1, 3, 5, 0, 9, 4, 6],
    [2, 7, 1, 3, 5, 0, 9, 4, 6, 8],
    [7, 1, 3, 5, 0, 9, 4, 6, 8, 2],
    [1, 3, 5, 0, 9, 4, 6, 8, 2, 7],
    [3, 5, 0, 9, 4, 6, 8, 2, 7, 1],
    [5, 0, 9, 4, 6, 8, 2, 7, 1, 3]
];

export const validateInvoiceReferenceNumberWithType = (referenceNumber: string, type: string): boolean => {
    switch (type) {
        case PAYMENT_INFO_TYPE.SCOR:
            return validateInvoiceReferenceNumberSCOR(referenceNumber);
        case PAYMENT_INFO_TYPE.QRR:
            return validateInvoiceReferenceNumberQRR(referenceNumber);
        default:
            return false;
    }
};

export const validateInvoiceReferenceNumberQRR = (referenceNumber: string): boolean => {
    const referenceNumberWithoutSpaces = referenceNumber.replace(/\s/g, "");

    // Check if the input is exactly 27 digits long and is a number
    if (
        referenceNumberWithoutSpaces.length !== 27 ||
        !/^\d{27}$/.test(referenceNumberWithoutSpaces) ||
        isNaN(Number(referenceNumberWithoutSpaces))
    ) {
        return false;
    }

    let carry = 0;

    // Process each digit except the last one
    for (let i = 0; i < 26; i++) {
        const digit = Number(referenceNumberWithoutSpaces[i]);
        carry = PRUEFZIFFER_MATRIX[carry][digit];
    }

    // Subtract the final carry from 10 to get the expected check digit
    const expectedCheckDigit = (10 - carry) % 10;

    // The last digit is the check digit we want to verify
    const checkDigit = Number(referenceNumberWithoutSpaces[26]);

    // Check if the final carry matches the check digit
    return expectedCheckDigit === checkDigit;
};

export const validateInvoiceReferenceNumberSCOR = (referenceNumber: string): boolean => {
    const referenceNumberWithoutSpaces = referenceNumber.replace(/\s/g, "");

    // Ensure the reference starts with 'RF' and is at least 4 characters long
    if (!/^RF\d{2}/.test(referenceNumberWithoutSpaces)) return false;

    // Move the first four characters to the end
    const rearranged = referenceNumberWithoutSpaces.slice(4) + referenceNumberWithoutSpaces.slice(0, 4);

    // Replace letters with their corresponding numbers (A=10, B=11, ..., Z=35)
    const expanded = rearranged
        .toUpperCase()
        .split("")
        .map((char) => {
            const code = char.charCodeAt(0);
            return code >= 65 && code <= 90 ? (code - 55).toString() : char;
        })
        .join("");

    // Convert the expanded string to a BigInt and perform the Modulo 97 operation
    const remainder = BigInt(expanded) % 97n;

    // The reference is valid if the remainder is 1
    return remainder === 1n;
};

export const formatReferenceNumberQRR = (referenceNumber: string): string => {
    return referenceNumber.replace(/(\d{2})(\d{5})(\d{5})(\d{5})(\d{5})(\d+)/, "$1 $2 $3 $4 $5 $6");
};

export const formatReferenceNumberSCOR = (referenceNumber: string): string => {
    const referenceNumberWithoutSpaces = referenceNumber.replace(/\s/g, "");

    // Group characters in blocks of four
    return referenceNumberWithoutSpaces.match(/.{1,4}/g)?.join(" ") ?? referenceNumberWithoutSpaces;
};

export const validateIBAN = (iban: string) => {
    const ibanWithoutSpaces = iban.replace(/\s/g, "");

    return isValidIBAN(ibanWithoutSpaces);
};

export const getIBANType = (iban: string): IBAN_TYPE => {
    const ibanWithoutSpaces = iban.replace(/\s/g, "");

    // Extract the QR-IID part (position 5 to 9, zero-based index 4 to 8)
    const qrIid = parseInt(ibanWithoutSpaces.substring(4, 9), 10);

    // Check if the QR-IID falls within the QR IBAN range
    if (qrIid >= 30000 && qrIid <= 31999) {
        return IBAN_TYPE.QR;
    }

    return IBAN_TYPE.NORMAL;
};

export const getPaymentInfoType = (iban: string, referenceNumber?: string): PAYMENT_INFO_TYPE => {
    const ibanWithoutSpaces = iban.replace(/\s/g, "");
    const hasReferenceNumber = referenceNumber && referenceNumber.length > 0;

    const ibanType = getIBANType(ibanWithoutSpaces);

    if (ibanType === IBAN_TYPE.NORMAL && !hasReferenceNumber) {
        return PAYMENT_INFO_TYPE.NON;
    } else if (ibanType === IBAN_TYPE.NORMAL && hasReferenceNumber) {
        return PAYMENT_INFO_TYPE.SCOR;
    } else if (ibanType === IBAN_TYPE.QR && hasReferenceNumber) {
        return PAYMENT_INFO_TYPE.QRR;
    } else {
        return PAYMENT_INFO_TYPE.NON;
    }
};

export const getDateStringForHasuraQuery = (date: any): string => {
    let locale: Locale = de;
    if (i18next.language.includes("de")) locale = de;
    if (i18next.language.includes("en")) locale = enGB;
    if (i18next.language.includes("fr")) locale = fr;
    if (i18next.language.includes("it")) locale = it;

    return format(date, "yyyy-MM-dd", { locale });
};

// returns the date in the format "yyyy-MM-dd" for the html element
export const getDateStringForFormElement = (inputDate: string): string => {
    return format(new Date(inputDate), "yyyy-MM-dd");
};

export const getDateString = (inputDate: string): string => {
    return format(new Date(inputDate), "dd.MM.yyyy");
};

export const getRoleNameByWorkflowUserRole = (role: WORKFLOWUSER_ROLE): string => {
    switch (role) {
        case WORKFLOWUSER_ROLE.VISA_1:
            return `${i18next.t("screens.kredi_flow.visa")} 1`;
        case WORKFLOWUSER_ROLE.VISA_2:
            return `${i18next.t("screens.kredi_flow.visa")} 2`;
        case WORKFLOWUSER_ROLE.VISA_3:
            return `${i18next.t("screens.kredi_flow.visa")} 3`;
        case WORKFLOWUSER_ROLE.ACCOUNTANT:
            return i18next.t("screens.kredi_flow.accountant");
        default:
            return "";
    }
};

export const hasTwoDecimalPlaces = (num: number): boolean => {
    return /^\d+\.\d{2}$/.test(num.toString());
};

export const getAmountWithTwoDecimalPlaces = (amount?: string): string | undefined => {
    const currentAmountNumber = Number(amount);

    let amountWithTwoDecimalPlaces = undefined;

    if (currentAmountNumber && !hasTwoDecimalPlaces(currentAmountNumber)) {
        amountWithTwoDecimalPlaces = currentAmountNumber.toFixed(2);
    }

    return amountWithTwoDecimalPlaces;
};

// Returns NetAmount (Betrag exkl. MWST)
export const calculateNetAmountFromGrossAmount = (
    grossAmount: number,
    vatRate: number,
    roundingPrecision: number = 5
): string => {
    const unroundedNetAmount = grossAmount / (1 + vatRate / 100);

    // Round to the specified precision (5 cents = 0.05, 1 cent = 0.01)
    const precisionValue = roundingPrecision / 100;
    const roundedNetAmount = Math.round(unroundedNetAmount / precisionValue) * precisionValue;

    return roundedNetAmount.toFixed(2);
};

export const calculateGrossAmountFromNetAmount = (
    netAmount: number,
    vatRate: number,
    roundingPrecision: number = 5
): string => {
    const unroundedGrossAmount = netAmount * (1 + vatRate / 100);

    // Round to the specified precision (5 cents = 0.05, 1 cent = 0.01)
    const precisionValue = roundingPrecision / 100;
    const roundedGrossAmount = Math.round(unroundedGrossAmount / precisionValue) * precisionValue;

    return roundedGrossAmount.toFixed(2);
};

// Vorsteueranteil (#24071): free input in percent, empty means "ERP default". A comma is accepted as decimal
// separator because that is what a Swiss keyboard produces on the numpad.
export const vatProportionToNumber = (vatProportion?: string): number | null => {
    const trimmed = vatProportion?.trim() ?? "";

    if (trimmed === "") {
        return null;
    }

    const parsed = Number(trimmed.replace(",", "."));

    return Number.isFinite(parsed) ? parsed : NaN;
};

export const isValidVatProportion = (vatProportion?: string): boolean => {
    const parsed = vatProportionToNumber(vatProportion);

    return parsed === null || (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100);
};

export const INVALID_FILENAME_CHARS = [
    "(",
    ")",
    "/",
    "\\",
    ":",
    "*",
    "?",
    "<",
    ">",
    "|",
    "{",
    "}",
    "[",
    "]",
    "%",
    "&",
    "#",
    "$",
    "~",
    "+",
    ";",
    ",",
    // eslint-disable-next-line quotes
    '"',
    "`",
    "^"
];

// Example QR-bill_(4)_pdf.pdf becomes QR-bill_4_pdf.pdf
// Replaces invalid filename characters with underscores, avoiding consecutive underscores
export const replaceInvalidFileNameChars = (str: string): string => {
    const chars = str.split("");
    const result: string[] = [];

    for (let i = 0; i < chars.length; i++) {
        const c = chars[i];
        if (INVALID_FILENAME_CHARS.includes(c)) {
            // Check what we have in the result so far (left)
            const prevIsUnderscore = result.length > 0 && result[result.length - 1] === "_";
            // Check the next character in the original string (right)
            const nextIsUnderscore = i + 1 < chars.length && chars[i + 1] === "_";

            if (prevIsUnderscore || nextIsUnderscore) {
                // If we already have an underscore on the left or right,
                // skip the invalid character entirely (don't push anything).
                continue;
            } else {
                // Otherwise replace this invalid character with a single underscore.
                result.push("_");
            }
        } else {
            // Normal character, just push it.
            result.push(c);
        }
    }

    return result.join("");
};

// Example: "äöüß" becomes "aouss"
// macOS/iOS-Dateinamen kommen häufig in NFD-Form an (z.B. 'U' + COMBINING DIAERESIS U+0308).
// Vor der Replace-Kette zwingend in NFC normalisieren, sonst greifen die Umlaut-Regexes nicht
// und das übrigbleibende Combining-Diacritic verletzt die Azure-Blob-Tag-Validierung
// (erlaubt sind nur a-z A-Z 0-9 Leerzeichen + - . / : = _) → 400 Bad Request beim Upload.
// Härtung: alles, was nach der Replace-Kette noch nicht-ASCII ist, durch '_' ersetzen.
export const replaceUmlauts = (str: string): string => {
    return str
        .normalize("NFC")
        .replace(/ä/g, "a")
        .replace(/ö/g, "o")
        .replace(/ü/g, "u")
        .replace(/Ä/g, "A")
        .replace(/Ö/g, "O")
        .replace(/Ü/g, "U")
        .replace(/ß/g, "ss")
        .replace(/é/g, "e")
        .replace(/è/g, "e")
        .replace(/ê/g, "e")
        .replace(/à/g, "a")
        .replace(/á/g, "a")
        .replace(/â/g, "a")
        .replace(/ç/g, "c")
        .replace(/[-￿]/g, "_");
};

export const formatDateForFile = (date: Date) => {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS");
};

export const formatAccountingAmountString = (amount: string | undefined): string => {
    if (!amount) return "";
    return `CHF ${formatCurrency(parseFloat(amount.replace(",", ".") ?? "0"))}`;
};

export const formatAmountNumberToCurrencyString = (amount: number | undefined): string => {
    if (amount === undefined) return "";

    return formatCurrency(amount);
};

// format a number to a string where there are apostrophies for the thousands. ie 1234567890 becomes 1'234'567'890
export const formatNumberWithApostrophies = (amount: string | undefined): string => {
    if (!amount) return "";
    return amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

// Whether an AI creditor match may fill the creditor field after the QR step ran (#26422).
// An empty field is always open. A creditor the QR step resolved by its IBAN stays authoritative;
// one it resolved only by a name/address lookup is weaker than an AI match and may be replaced,
// as long as the field still holds exactly that QR creditor.
export const canAiApplyCreditor = (
    currentCreditorId: string | undefined,
    qrResult: Pick<IQRExtractInvoiceData, "creditorid" | "creditormatchsource"> | undefined
): boolean => {
    if (!currentCreditorId) return true;

    return qrResult?.creditormatchsource === "nameAddress" && qrResult.creditorid === currentCreditorId;
};
